import type { MenuLayout } from '../api/types';

import type { NavItem, NavSection } from './types';

export type { MenuLayout };

/** Sections in the order the menu shows them. */
export const MENU_SECTIONS: readonly NavSection[] = ['primary', 'main', 'admin'];

/** The section an item belongs to (`main` unless it says otherwise). */
export const sectionOf = (item: Pick<NavItem, 'section'>): NavSection => item.section ?? 'main';

/** Whether a layout arranges nothing. */
export const isEmptyLayout = (layout: MenuLayout | null | undefined): boolean =>
  !layout || (layout.order.length === 0 && layout.hidden.length === 0);

/** Compares two layouts (null and an empty layout are the same arrangement). */
export function sameLayout(a: MenuLayout | null | undefined, b: MenuLayout | null | undefined): boolean {
  if (isEmptyLayout(a) || isEmptyLayout(b)) return isEmptyLayout(a) && isEmptyLayout(b);
  const same = (x: readonly string[], y: readonly string[]) =>
    x.length === y.length && x.every((v, i) => v === y[i]);
  return (
    same(a?.order ?? [], b?.order ?? []) && same([...(a?.hidden ?? [])].sort(), [...(b?.hidden ?? [])].sort())
  );
}

/**
 * Puts the items in the order of the layout. Items the layout does not name (a module added later, or items the person could not
 * see when they arranged it) go to their natural place: right after the closest item before them in the natural order that is
 * already placed. Ids the layout names but the items do not have are ignored.
 */
export function arrangeItems<T extends Pick<NavItem, 'id'>>(
  naturalItems: readonly T[],
  layout: MenuLayout | null | undefined,
): T[] {
  const byId = new Map(naturalItems.map((item) => [item.id, item]));
  const result: T[] = [];
  const placed = new Set<string>();
  for (const id of layout?.order ?? []) {
    const item = byId.get(id);
    if (item && !placed.has(id)) {
      result.push(item);
      placed.add(id);
    }
  }
  naturalItems.forEach((item, index) => {
    if (placed.has(item.id)) return;
    // The closest item before it in the natural order that is already placed: it goes right after that one.
    const before = naturalItems.slice(0, index).findLast((candidate) => placed.has(candidate.id));
    result.splice(before ? result.indexOf(before) + 1 : 0, 0, item);
    placed.add(item.id);
  });
  return result;
}

export interface ArrangedMenu<T> {
  /** Every item, in the arranged order. */
  all: T[];
  /** The items the menu shows. */
  shown: T[];
  /** The items the layout hides. */
  hidden: T[];
}

/** The arranged items split into the shown and the hidden ones. */
export function arrangeMenu<T extends Pick<NavItem, 'id'>>(
  naturalItems: readonly T[],
  layout: MenuLayout | null | undefined,
): ArrangedMenu<T> {
  const hiddenIds = new Set(layout?.hidden ?? []);
  const all = arrangeItems(naturalItems, layout);
  return {
    all,
    shown: all.filter((item) => !hiddenIds.has(item.id)),
    hidden: all.filter((item) => hiddenIds.has(item.id)),
  };
}

/** The layout of an arrangement: sections one after another (the menu never moves an item out of its section), plus the hidden ids. */
export function layoutOf(
  arranged: readonly Pick<NavItem, 'id' | 'section'>[],
  hiddenIds: ReadonlySet<string> | readonly string[],
): MenuLayout {
  const hidden = new Set(hiddenIds);
  const order = MENU_SECTIONS.flatMap((section) =>
    arranged.filter((item) => sectionOf(item) === section).map((item) => item.id),
  );
  return { order, hidden: order.filter((id) => hidden.has(id)) };
}

/** Moves the item with `id` to the place of `overId` within its section (a drag, or a step with the arrow buttons). */
export function moveItem<T extends Pick<NavItem, 'id' | 'section'>>(
  arranged: readonly T[],
  id: string,
  overId: string,
): T[] {
  const item = arranged.find((candidate) => candidate.id === id);
  const target = arranged.find((candidate) => candidate.id === overId);
  if (!item || !target || id === overId || sectionOf(item) !== sectionOf(target)) return [...arranged];
  const section = sectionOf(item);
  const inSection = arranged.filter((candidate) => sectionOf(candidate) === section);
  const from = inSection.indexOf(item);
  const to = inSection.indexOf(target);
  const moved = [...inSection];
  moved.splice(from, 1);
  moved.splice(to, 0, item);
  const rest = [...moved];
  return arranged.map((candidate) =>
    sectionOf(candidate) === section ? (rest.shift() ?? candidate) : candidate,
  );
}
